// StepWizard.jsx
import React, { useState, useEffect, useRef } from 'react';
import ResumeUpload from './steps/ResumeUpload';
import SearchParameters from './steps/SearchParameters';
import MatchResults from './steps/MatchResults';
import PromptRefinement from './PromptRefinement';
import BaseLayout from './BaseLayout';
import axios from 'axios';
import emailGeneratorService from '../services/emailGeneratorService';
import { userService } from '../services/userService';
import OnboardingModal from './OnboardingModal';
import { motion, AnimatePresence } from 'framer-motion';
import AppBar from './AppBar';
import PricingModal from './PricingModal';
import EmailGeneratorModals from './modals/EmailGeneratorModals';

import { MAIN_BG_GRADIENT } from '../utils/constants';
import Modal from './ui/modal';
import ChatbotQuery from './ChatbotQuery';
import GuidedTour from './GuidedTour';
import UIBlockerModal from './UIBlockerModal';
import SearchPage from './steps/SearchPage';
import { Menu, X as CloseIcon } from 'lucide-react';

// Animation variants for step transitions
const stepVariants = {
  enter: {
    opacity: 0,
    scale: 0.95,
  },
  center: {
    zIndex: 1,
    opacity: 1,
    scale: 1,
  },
  exit: {
    zIndex: 0,
    opacity: 0,
    scale: 0.95,
  },
};

const stepTransition = {
  opacity: { duration: 0.3, ease: "easeInOut" },
  scale: { duration: 0.3, ease: "easeInOut" },
};

const stepIndicatorVariants = {
  initial: { scale: 0.8, opacity: 0, y: 20 },
  animate: (i) => ({
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  }),
  hover: {
    scale: 1.1,
    y: -2,
    transition: { type: "spring", stiffness: 400, damping: 10 },
  },
  tap: {
    scale: 0.95,
    y: 0,
  },
  active: {
    scale: [1.05, 1.1, 1.05],
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 300, 
      damping: 20,
      scale: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
  },
};

const progressLineVariants = {
  initial: { scaleX: 0 },
  animate: (completed) => ({
    scaleX: completed ? 1 : 0,
    transition: {
      duration: 0.6,
      ease: "easeInOut",
      delay: 0.2,
    },
  }),
};

const StepWizard = () => {
  const [step, setStep] = useState(1);
  const [isAppBarVisible, setIsAppBarVisible] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [parsedJson, setParsedJson] = useState(null);
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState(null);

  const [connectionPoints, setConnectionPoints] = useState(null);
  const [searchRecommendations, setSearchRecommendations] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [reasons, setReasons] = useState({});
  const [inversePrompts, setInversePrompts] = useState([]);
  const [inverseMatches, setInverseMatches] = useState([]);
  const [totalFound, setTotalFound] = useState(0);

  const [refinedSearch, setRefinedSearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const [matchLimit, setMatchLimit] = useState(150);

  // Add new state for tracking API progress
  const [apiProgress, setApiProgress] = useState({
    refinePrompt: false,
    quisSearch: false,
    quisRank: false
  });
  const [currentApiStep, setCurrentApiStep] = useState('');
  
  // Add state for rotating loading messages
  const [rotatingMessage, setRotatingMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);

  const [error, setError] = useState(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [networkHistory, setNetworkHistory] = useState([]);
  const [isLoadedFromHistory, setIsLoadedFromHistory] = useState(false);

  // Add state for reasons loading and match reasons
  const [loadingReasons, setLoadingReasons] = useState({});
  const [matchReasons, setMatchReasons] = useState({});
  const [connectionReasons, setConnectionReasons] = useState({});

  // Lift visibleCount state here for progress calculation and pass to MatchResults
  const [visibleCount, setVisibleCount] = useState(20);

  // Track if search history has been saved for this set of matches
  const [hasSavedSearchHistory, setHasSavedSearchHistory] = useState(false);
  const hasSavedSearchHistoryRef = React.useRef(false);

  // In StepWizard, add state to control UIBlockerModal visibility
  const [showBlockerModal, setShowBlockerModal] = useState(false);
  
  // Floating button state
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [emailContainerAnimated, setEmailContainerAnimated] = useState(false);
  const emailContainerRef = useRef(null);

  // Add state for chatbot answers
  const [chatbotAnswers, setChatbotAnswers] = useState(null);

  const [intentValue, setIntentValue] = useState('');

  // Navigation functions
  const goToNext = () => {
    setStep((prev) => prev + 1);
  };
  
  const goToPrev = () => {
    setStep((prev) => prev - 1);
  };

  const goToStep = (newStep) => {
    if(newStep!=3)
    setStep(newStep);
  };

  // Function to determine if a step is accessible
  const isStepAccessible = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return true; // Always accessible
      case 2:
        return parsedJson !== null; // Requires resume data
      case 3:
        return searchQuery !== ''; // Requires search query
      case 4:
        return matches.length > 0; // Requires matches
      default:
        return false;
    }
  };

  // Calculate progress for reasons loading (only when on step 4)
  let isAnyReasonLoading = false;
  let progressPercent = 0;
  if (step === 4 && matches.length > 0) {
    const visibleMatches = matches.slice(0, visibleCount);
    // Only consider unlocked profiles for progress
    const unlockedLimit = (userSubscription?.tier !== 'starter' && userSubscription?.tier !== 'free') ? visibleMatches.length : Math.min(5, visibleMatches.length);
    const unlockedMatches = visibleMatches.slice(0, unlockedLimit);
    const totalToLoad = unlockedMatches.length * 2; // 2 API calls per match
    let loadedCount = 0;
    
    // Ensure matchReasons and connectionReasons are objects
    const safeMatchReasons = typeof matchReasons === 'object' && !Array.isArray(matchReasons) ? matchReasons : {};
    const safeConnectionReasons = typeof connectionReasons === 'object' && !Array.isArray(connectionReasons) ? connectionReasons : {};
    
    unlockedMatches.forEach(match => {
      const matchKey = match.name || match.profile_name || 'Unknown';
      if (safeMatchReasons[matchKey] !== undefined || match.match_reasons !== undefined) loadedCount++;
      if (safeConnectionReasons[matchKey] !== undefined || match.reason !== undefined) loadedCount++;
    });
    progressPercent = totalToLoad === 0 ? 0 : Math.round((loadedCount / totalToLoad) * 100);
    isAnyReasonLoading = totalToLoad > 0 && loadedCount < totalToLoad;
    
  }


  // Additional fallback: If progress is 100% but blocker is still showing, hide it after 5 seconds
  useEffect(() => {
    let timeoutId;
    if (step === 4 && progressPercent === 100 && showBlockerModal && !isAnyReasonLoading) {
      timeoutId = setTimeout(() => {
        console.log('Progress is 100% but blocker still showing, forcing hide');
        setShowBlockerModal(false);
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timeoutId);
  }, [step, progressPercent, showBlockerModal, isAnyReasonLoading]);

  // Scroll listener to show button when user scrolls away from email container
  useEffect(() => {
    if (step === 4 && buttonClicked && selectedContacts.length > 0) {
      const handleScroll = () => {
        if (emailContainerRef.current) {
          const element = emailContainerRef.current;
          const elementRect = element.getBoundingClientRect();
          const isContainerVisible = elementRect.top < window.innerHeight && elementRect.bottom > 0;
          
          // If container is not fully visible, show button again
          if (!isContainerVisible) {
            setButtonClicked(false);
          }
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [step, buttonClicked, selectedContacts.length]);

  // Check if this is the user's first visit
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
      localStorage.setItem('hasSeenOnboarding', 'true');
    }

    // Read shouldAutoStartTour from localStorage on mount
    const shouldAuto = localStorage.getItem('shouldAutoStartTour') === 'true';
    setShouldAutoStartTour(shouldAuto);
    console.log(`[StepWizard] Initial mount: shouldAutoStartTour=${shouldAuto}`);
  }, []);



  const handleHistoryItemClick = async (guid) => {
    setIsSidebarOpen(false); // Close sidebar immediately
    setLoading(true);
    setStep(4); // Switch to the results view to show the loader
    setIsLoadedFromHistory(true);
    setShowBlockerModal(true);

    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) return;

      const historyItem = await userService.getSearchHistoryItem(currentUser.uid, guid);

      // Assuming historyItem contains matches and query info
      setSearchQuery(historyItem.search_query);
      // If matches have match_reasons, extract them for matchReasons state
      if (historyItem.matches && historyItem.matches.length > 0 && Object.prototype.hasOwnProperty.call(historyItem.matches[0], 'match_reasons')) {
        // Build matchReasons object from match_reasons in matches
        const matchReasonsFromMatches = {};
        historyItem.matches.forEach(match => {
          if (match.name && Object.prototype.hasOwnProperty.call(match, 'match_reasons')) {
            matchReasonsFromMatches[match.name] = match.match_reasons;
          }
        });
        setMatchReasons(matchReasonsFromMatches);
      } else if (historyItem.reasons) {
        // Ensure reasons is an object, not an array
        const reasonsObj = Array.isArray(historyItem.reasons) ? {} : historyItem.reasons;
        setMatchReasons(reasonsObj);
      } else {
        setMatchReasons({});
      }
      setMatches(historyItem.matches || []);
      setInverseMatches(historyItem.inverse_matches || []);
      setInversePrompts(historyItem.inverse_prompts || []);
      setTotalFound(historyItem.total_found || historyItem.matches?.length || 0);

      if (historyItem.parsed_resume) {
        setResumeData({ parsed: { resume: historyItem.parsed_resume } });
      }

      if (historyItem.connection_reasons) {
        // Ensure connection_reasons is an object, not an array
        const connectionReasonsObj = Array.isArray(historyItem.connection_reasons) ? {} : historyItem.connection_reasons;
        setConnectionReasons(connectionReasonsObj);
      } else {
        setConnectionReasons({});
      }

      // Handle search recommendations from history
      if (historyItem.search_recommendations) {
        setSearchRecommendations(historyItem.search_recommendations);
      } else {
        setSearchRecommendations([]);
      }

    } catch (error) {
      console.error("Error loading history item:", error);
      setError(error);
    } finally {
      setLoading(false); // We have the data or an error, so not loading
    }
  };

  const handleOnboardingClose = () => {
    console.log('[StepWizard] handleOnboardingClose called.');
    setShowOnboarding(false);
    setShouldAutoStartTour(true); // Set flag to auto-start tour after UI blocker
    localStorage.setItem('shouldAutoStartTour', 'true'); // Persist in localStorage
    console.log('[StepWizard] shouldAutoStartTour set to true and saved to localStorage.');
  };

  const handleShowEmailModal = (data) => {
    setEmailModalData(data);
    setShowEmailModal(true);
  };

  const handleResumeContinue = async (responseData) => { // Renamed 'data' to 'responseData' for clarity
    console.log("Data received from SearchPage:", responseData); // Log the full response

    // Handle the new structure where connectionPoints is already an array
    let itemsList = [];
    if (responseData.parsed.connectionPoints) {
      if (Array.isArray(responseData.parsed.connectionPoints)) {
        // New format: connectionPoints is already an array of objects with 'item' property
        itemsList = responseData.parsed.connectionPoints.map(point => point.item || point);
      } else if (responseData.parsed.connectionPoints.connection_points) {
        // Legacy format: nested structure
        itemsList = responseData.parsed.connectionPoints.connection_points.map(point => point.item);
      }
    }

    // Handle search recommendations
    let searchRecommendationsList = [];
    if (responseData.parsed.searchRecommendations) {
      if (Array.isArray(responseData.parsed.searchRecommendations)) {
        // New format: searchRecommendations is already an array of objects with 'recommendation' property
        searchRecommendationsList = responseData.parsed.searchRecommendations.map(rec => rec.recommendation || rec);
      } else if (responseData.parsed.searchRecommendations.search_recommendations) {
        // Legacy format: nested structure
        searchRecommendationsList = responseData.parsed.searchRecommendations.search_recommendations.map(rec => rec.recommendation);
      }
    }

    setConnectionPoints(itemsList);
    setSearchRecommendations(searchRecommendationsList);
    setResumeData(responseData);         // Store the whole response if needed elsewhere
    setParsedJson(responseData.data);    // Correctly access the nested 'data' object for parsed content
    setSearchQuery(responseData.searchQuery); // Set the query for both flows

    // ROUTING LOGIC: Decide where to go based on the search type.
    if (responseData.isDeepSearch) {
      // For a "Deep Search", go to the chatbot (Step 3).
      // The ChatbotQuery component will use the `searchQuery` state as its initial query.
      setStep(3);
    } else if (responseData.searchQuery && responseData.searchQuery.trim() !== '') {
      // For a "Search Now", go directly to results (Step 4) by calling handleSearch.
      await handleSearch(responseData.searchQuery, matchLimit);
    } else {
      // Fallback to original flow if for some reason searchQuery is not there.
      // This path shouldn't be taken with the current UI, but it's a safe fallback.
      goToNext();
    }
  };

  const handleSearch = async (query, matchCount, selectedProfiles = []) => {
    setMatchLimit(matchCount);
    setSearchQuery(query);
    setLoadingPercentage(0);
    setLoading(true);
    setLoadingStep(0);
    setStep(4);
    setIsLoadedFromHistory(false);
    
    // Reset API progress tracking
    setApiProgress({
      refinePrompt: false,
      quisSearch: false,
      quisRank: false
    });
    setCurrentApiStep('');
    setRotatingMessage(''); // Reset rotating message
    
    // Reset reasons state to prevent stale data issues
    setMatchReasons({});
    setConnectionReasons({});
    setLoadingReasons({});

    try {
      // Get Firebase auth token
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error('No authenticated user found');
        return;
      }

      const authToken = await currentUser.getIdToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      };

      // Prepare selected profiles data
      const selectedProfilesData = selectedProfiles.map(profile => ({
        id: profile.id || '',
        name: profile.name || '',
        position: profile.position || '',
        company: profile.company || '',
        email: profile.email || '',
        url: profile.url || profile.linkedin || ''
      }));

      // Step 1: Call v2 refine-prompt endpoint with selected profiles
      setCurrentApiStep('Refining your search query...');
      const v2Response = await axios.post(
        `${process.env.REACT_APP_REFINE_QUERY_URL}/api/v2/refine-prompt`,
        {
          originalPrompt: query,
          selectedProfiles: selectedProfilesData
        },
        {
          withCredentials: true,
          headers: headers
        }
      );
      
      // Mark refine-prompt as complete
      setApiProgress(prev => ({ ...prev, refinePrompt: true }));
      setCurrentApiStep('Searching for matches...');

      // Use the new promptService to generate inverse prompts
      const userData = {
        name: currentUser.displayName || 'Unknown',
        email: currentUser.email || 'No email'
      };

      let inversePromptsResponse = { inverse_prompts: [] };
      let inverseMatchesResponse = { matches: [] };
      if (userSubscription?.tier !== 'starter') {
        try {
          // inversePromptsResponse = await promptService.generateInversePrompts(query, userData);
          // console.log('Inverse prompts generated:', inversePromptsResponse);
        } catch (error) {
          console.error('Error generating inverse prompts:', error);
          inversePromptsResponse = { inverse_prompts: [] }; // Fallback to empty array
        }

        // Store inverse prompts
        setInversePrompts(inversePromptsResponse.inverse_prompts || []);

        // NEW: Find inverse matches (users looking for someone like you)
        try {
          // inverseMatchesResponse = await promptService.findInverseMatches(query, 10, 0.7);
          // console.log('Inverse matches found:', inverseMatchesResponse);
          // setInverseMatches(inverseMatchesResponse.matches || []);
        } catch (error) {
          console.error('Error finding inverse matches:', error);
          setInverseMatches([]);
        }
      } else {
        setInversePrompts([]);
        setInverseMatches([]);
      }

      // Call fetchData with the v2 response and the original query
      await fetchData({
        query: v2Response.data,
        searchParams: {},
      }, query); // Pass the original query as second parameter
    } catch (error) {
      console.error('Error in handleSearch:', error);
      // Mark all APIs as complete on error to stop loading
      setApiProgress({
        refinePrompt: true,
        quisSearch: true,
        quisRank: true
      });
      setCurrentApiStep('Error occurred while processing...');
      setLoading(false);
    }
  };

  const getRandomLoadingMessage = () => {
    const baseMessages = [
      'Analyzing your search criteria...',
      'Orchestrating microservice calls...',
      'Fetching matches from database...',
      'Optimizing your experience...',
      'Executing graph traversal algorithms...',
      'Processing your request...',
      'Calibrating recommendation engine parameters...',
      'Updating your connections...',
      'Securing your data...',
      'Discovering new opportunities...',
      'Indexing semantic similarity vectors...',
      'Deserializing polymorphic data streams...',
      'Compiling optimized query plans...',
      'Persisting distributed session state...',
      'Normalizing heterogeneous data schemas...',
      'Initiating asynchronous data pipelines...',
      'Aggregating real-time telemetry metrics...',
      'Applying People-Search inference models...',
      'Validating cross-service API contracts...',
      'Hashing sensitive attributes...',
      'Spinning up ephemeral compute instances...',
      'Synchronizing distributed cache replicas...',
      'Reconciling data consistency...',
      'Performing integrity checks...',
      'Provisioning network resources...',
      'Validating OAuth2 token integrity...',
      'Performing bitwise integrity checks...',
      'Calibrating deep learning inference models...'
    ];

    // Add personalized message with random connection points
    let personalizedMessage = 'Analyzing your resume...';
    if (connectionPoints && connectionPoints.length > 0) {
      // Get 1-3 random connection points
      const numPoints = Math.min(Math.floor(Math.random() * 3) + 1, connectionPoints.length);
      const shuffled = [...connectionPoints].sort(() => 0.5 - Math.random());
      const selectedPoints = shuffled.slice(0, numPoints);
      personalizedMessage = `Analyzing your data: ${selectedPoints.join(', ')}...`;
    }

    // Combine messages and ensure personalized message is included
    const messages = [...baseMessages, personalizedMessage];

    // If we have connectionPoints, give the personalized message a higher chance of appearing
    if (connectionPoints && connectionPoints.length > 0) {
      // Add the personalized message multiple times to increase its probability
      messages.push(personalizedMessage, personalizedMessage);
    }

    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  };

  const getLoadingMessage = () => {
    // Show current API step if available
    if (currentApiStep) {
      return currentApiStep;
    }

    const baseMessages = [
      'Analyzing your search criteria...',
      'Orchestrating microservice calls...',
      'Fetching matches from database...',
      'Optimizing your experience...',
      'Executing graph traversal algorithms...',
      'Processing your request...',
      'Calibrating recommendation engine parameters...',
      'Updating your connections...',
      'Securing your data...',
      'Discovering new opportunities...',
      'Indexing semantic similarity vectors...',
      'Deserializing polymorphic data streams...',
      'Compiling optimized query plans...',
      'Persisting distributed session state...',
      'Normalizing heterogeneous data schemas...',
      'Initiating asynchronous data pipelines...',
      'Aggregating real-time telemetry metrics...',
      'Applying People-Search inference models...',
      'Validating cross-service API contracts...',
      'Hashing sensitive attributes...',
      'Spinning up ephemeral compute instances...',
      'Synchronizing distributed cache replicas...',
      'Reconciling data consistency...',
      'Performing integrity checks...',
      'Provisioning network resources...',
      'Validating OAuth2 token integrity...',
      'Performing bitwise integrity checks...',
      'Calibrating deep learning inference models...'
    ];

    // Add personalized message with random connection points
    let personalizedMessage = 'Analyzing your resume...';
    if (connectionPoints && connectionPoints.length > 0) {
      // Get 1-3 random connection points
      const numPoints = Math.min(Math.floor(Math.random() * 3) + 1, connectionPoints.length);
      const shuffled = [...connectionPoints].sort(() => 0.5 - Math.random());
      const selectedPoints = shuffled.slice(0, numPoints);
      personalizedMessage = `Analyzing your data: ${selectedPoints.join(', ')}...`;
    }

    // Combine messages and ensure personalized message is included
    const messages = [...baseMessages, personalizedMessage];

    // If we have connectionPoints, give the personalized message a higher chance of appearing
    if (connectionPoints && connectionPoints.length > 0) {
      // Add the personalized message multiple times to increase its probability
      messages.push(personalizedMessage, personalizedMessage);
    }

    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  };

  // Add useEffect for rotating loading messages
  useEffect(() => {
    if (loading) {
      const messages = [
        'Analyzing your search criteria...',
        'Orchestrating microservice calls...',
        'Fetching matches from database...',
        'Optimizing your experience...',
        'Executing graph traversal algorithms...',
        'Processing your request...',
        'Calibrating recommendation engine parameters...',
        'Updating your connections...',
        'Securing your data...',
        'Discovering new opportunities...',
        'Indexing semantic similarity vectors...',
        'Deserializing polymorphic data streams...',
        'Compiling optimized query plans...',
        'Persisting distributed session state...',
        'Normalizing heterogeneous data schemas...',
        'Initiating asynchronous data pipelines...',
        'Aggregating real-time telemetry metrics...',
        'Applying People-Search inference models...',
        'Validating cross-service API contracts...',
        'Hashing sensitive attributes...',
        'Spinning up ephemeral compute instances...',
        'Synchronizing distributed cache replicas...',
        'Reconciling data consistency...',
        'Performing integrity checks...',
        'Provisioning network resources...',
        'Validating OAuth2 token integrity...',
        'Performing bitwise integrity checks...',
        'Calibrating deep learning inference models...'
      ];

      const updateMessage = () => {
        const randomIndex = Math.floor(Math.random() * messages.length);
        setRotatingMessage(messages[randomIndex]);
        // Schedule next update with random duration between 3-5 seconds for faster rotation
        const nextUpdateDelay = Math.floor(Math.random() * (5000 - 3000 + 1)) + 3000;
        setTimeout(updateMessage, nextUpdateDelay);
      };

      // Start the first update immediately
      const randomIndex = Math.floor(Math.random() * messages.length);
      setRotatingMessage(messages[randomIndex]);
      const initialDelay = Math.floor(Math.random() * (5000 - 3000 + 1)) + 3000;
      const timeoutId = setTimeout(updateMessage, initialDelay);

      return () => clearTimeout(timeoutId);
    } else {
      // Clear the message when not loading
      setRotatingMessage('');
    }
  }, [loading]); // Only depend on loading state

  // Add useEffect for percentage loading based on API progress
  useEffect(() => {
    if (loading) {
      // Calculate percentage based on completed API calls
      const completedApis = Object.values(apiProgress).filter(Boolean).length;
      const totalApis = 3; // refine-prompt, quis-search, quis-rank
      const percentage = Math.floor((completedApis / totalApis) * 100);
      
      setLoadingPercentage(percentage);
      
      // If all APIs are complete, set to 100% and stop loading
      if (completedApis === totalApis) {
        setLoadingPercentage(100);
        setLoading(false);
      }
    } else {
      // Reset when not loading
      setLoadingPercentage(0);
    }
  }, [loading, apiProgress]);

  const fetchData = async (originalData, query) => {
    console.log("Starting Turbopuffer search flow");

    // Get Firebase auth token for Cloud Run authentication
    let authToken = '';
    try {
      // Import Firebase auth if not already imported at the top of the file
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        authToken = await currentUser.getIdToken();
        console.log('Auth token generated:', authToken ? 'Token exists' : 'No token');
        console.log('Token length:', authToken ? authToken.length : 0);
        console.log('Token first 10 chars:', authToken ? authToken.substring(0, 10) + '...' : 'No token');
      } else {
        console.warn('No authenticated user found. Proceeding without authentication token.');
      }
    } catch (error) {
      console.error('Error getting authentication token:', error);
    }

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    };

    console.log('Request headers:', headers);

    try {
      setLoadingStep(0);

      // Extract structured query from the refine-prompt response
      let structuredQuery;
      const refineResponse = originalData.query;
      
      if (refineResponse.query) {
        // Pass the query directly to quis-search - backend will handle decryption if needed
        structuredQuery = refineResponse.query;
        console.log('Using query from refine-prompt response (backend will handle decryption if needed)');
      } else {
        throw new Error('No structured query received from refine-prompt');
      }

      console.log('Structured query:', structuredQuery);

      // Step 2: Call /api/v2/quis-search with the structured query
      const quisSearchUrl = `${process.env.REACT_APP_REFINE_QUERY_URL}/api/v2/quis-search`;
      console.log("Calling quis-search at URL:", quisSearchUrl);
      
      const quisSearchData = {
        structuredQuery: structuredQuery,
        top_k: matchLimit || 150,
        include_attributes: true,
        originalPrompt: query  // Pass the original query for VIP notifications
      };

      const response = await axios.post(quisSearchUrl, quisSearchData, { headers });
      
      // Mark quis-search as complete
      setApiProgress(prev => ({ ...prev, quisSearch: true }));
      setCurrentApiStep('Ranking and analyzing results...');
      console.log('Quis search response:', response.data);
      
      // Check if the enhanced response from /quis-search includes internal quis-rank-and-query results
      const enhancedResults = response.data;
      
      if (enhancedResults.queried_profiles) {
        console.log('✅ Using enhanced /quis-search response with internal quis-rank-and-query results:');
        console.log('  - Queried profiles count:', enhancedResults.queried_profiles.length);
        
        // Use the queried results for display (they have full profile data)
        setMatches(enhancedResults.queried_profiles);
        setTotalFound(enhancedResults.queried_profiles.length);
        setVisibleCount(Math.min(enhancedResults.queried_profiles.length, 150));
        
        // Mark quis-rank as complete
        setApiProgress(prev => ({ ...prev, quisRank: true }));
        setCurrentApiStep('Finalizing results...');
      } else {
        console.log('⚠️ Enhanced /quis-search response not available, using original results');
        console.log('  - Original results count:', enhancedResults.queried_profiles?.length || 0);
        
        // Fallback to original results if available
        const fallbackResults = enhancedResults.queried_profiles || [];
        setMatches(fallbackResults);
        setTotalFound(fallbackResults.length);
        setVisibleCount(Math.min(fallbackResults.length, 150));
        
        // Mark quis-rank as complete
        setApiProgress(prev => ({ ...prev, quisRank: true }));
        setCurrentApiStep('Finalizing results...');
      }

      // 3. Only after /quis-rank is done, proceed to any further API calls or UI updates
      // (e.g., analyze-profiles, get-reasons, get-match-reasons, etc.)

      // Clear any previous errors
      setError(null);

      // Note: Quis search doesn't return reasons like BigQuery did
      // For now, we'll set empty reasons since quis-rank handles the relevance scoring
      setReasons({});

    } catch (error) {
      setError(error); // Set the error state
      if (error.response) {
        console.error('Error Response:', error.response.data);
        console.error('Error Status:', error.response.status);
      } else if (error.request) {
        console.error('Error Request:', error.request);
      } else {
        console.error('Error:', error.message);
      }
      // Mark all APIs as complete on error to stop loading
      setApiProgress({
        refinePrompt: true,
        quisSearch: true,
        quisRank: true
      });
      setCurrentApiStep('Error occurred while processing...');
    } finally {
      // Loading will be automatically set to false when all APIs are complete
      // via the useEffect that watches apiProgress
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get Firebase auth token
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.error('No authenticated user found');
        return;
      }

      // Send message using emailGeneratorService
      await emailGeneratorService.sendSupportMessage(message, currentUser.email);

      setSubmitSuccess(true);
      setMessage('');

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowHelpDialog(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFabClick = () => {
    setIsRotating(true);
    setShowHelpDialog(!showHelpDialog);
    // Reset rotation state after animation completes
    setTimeout(() => setIsRotating(false), 300);
  };

  // Update useEffect to use userService
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          console.error('No authenticated user found');
          setIsLoadingSubscription(false);
          return;
        }

        const subscriptionData = await userService.getUserSubscription(currentUser.uid);
        setUserSubscription(subscriptionData);
        console.log("USER SUBSCRIPTION DATA")
        console.log(subscriptionData)
        console.log('User subscription status:', subscriptionData);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  // Add mouse glow effect
  useEffect(() => {
    const glow = document.getElementById("mouse-glow");
    const update = (e) => {
      glow.style.transform = `translate(${e.clientX - 150}px, ${e.clientY - 150}px)`;
    };
    document.addEventListener("mousemove", update);
    return () => document.removeEventListener("mousemove", update);
  }, []);


  // Effect: Save search history after all reasons are loaded and UI blocker is gone
  useEffect(() => {
    // Only require reasons for visible matches
    const visibleMatches = matches.slice(0, visibleCount);
    const allAllowedReasonsLoaded = visibleMatches.length > 0 && visibleMatches.every(match => matchReasons[match.name] || match.match_reasons !== undefined);
    if (
      step === 4 &&
      matches.length > 0 &&
      !isAnyReasonLoading &&
      !hasSavedSearchHistoryRef.current &&
      userSubscription &&
      userSubscription.email && // ensure user data is loaded
      allAllowedReasonsLoaded
    ) {
      // Prepare matches with only the fields used in MatchResults
      const matchesWithReasons = matches.map(match => ({
        id: match.id,
        name: match.name,
        position: match.position,
        company: match.company,
        current_company_name: match.current_company_name,
        likely_email_1: match.likely_email_1,
        url: match.url || '',
        about: match.about || '',
        experience: Array.isArray(match.experience)
          ? match.experience
          : (match.experience && Array.isArray(match.experience.list) ? match.experience.list : []),
        education: Array.isArray(match.education)
          ? match.education
          : (match.education && Array.isArray(match.education.list) ? match.education.list : []),
        organizations: Array.isArray(match.organizations)
          ? match.organizations
          : (match.organizations && Array.isArray(match.organizations.list) ? match.organizations.list : []),
        current_education: match.current_education || '',
        match_score: match.match_score,
        matched_keywords: match.matched_keywords,
        is_dummy: match.is_dummy,
        locked: match.locked,
        match_reasons: matchReasons[match.name] !== undefined ? matchReasons[match.name] : match.match_reasons || null,
        reason: connectionReasons[match.name]
          ? { Reason: Array.isArray(connectionReasons[match.name]) ? connectionReasons[match.name] : (connectionReasons[match.name] != null ? String(connectionReasons[match.name]).split('\n') : []) }
          : match.reason || null
      }));
      // Prepare search history data
      const searchHistoryData = {
        search_query: searchQuery,
        matches: matchesWithReasons,
        inverse_matches: inverseMatches,
        inverse_prompts: inversePrompts,
        parsed_resume: resumeData?.parsed?.resume,
        reasons: matchReasons,
        connection_reasons: connectionReasons,
        search_recommendations: searchRecommendations,
      };
      // Save to backend
      (async () => {
        try {
          hasSavedSearchHistoryRef.current = true; // <-- set ref immediately to block further calls
          const { getAuth } = await import('firebase/auth');
          const auth = getAuth();
          const currentUser = auth.currentUser;
          if (!currentUser) return;
          await userService.saveSearchHistory(currentUser.uid, searchHistoryData);
          setHasSavedSearchHistory(true);
          console.log('Search history saved successfully from StepWizard.');
        } catch (e) {
          console.error('Failed to save search history from StepWizard:', e);
        }
      })();
    }
    // Reset flag if matches change or step changes
    if (step !== 4 || matches.length === 0) {
      setHasSavedSearchHistory(false);
      hasSavedSearchHistoryRef.current = false;
    }
  }, [step, matches, isAnyReasonLoading, userSubscription, searchQuery, inverseMatches, inversePrompts, resumeData, matchReasons, connectionReasons, searchRecommendations]);

  // Handler for editing the search query from MatchResults
  const handleEditSearchQuery = async (newQuery) => {
    setSearchQuery(newQuery);
    setLoadingPercentage(0);
    setLoading(true);
    setLoadingStep(0);
    setStep(4);
    setIsLoadedFromHistory(false);
    await handleSearch(newQuery, matchLimit);
  };

  // Guided tour state and steps (now at StepWizard level)
  const [tourVisible, setTourVisible] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  // All possible steps
  const allTourSteps = [
    {
      target: '#tour-search-query',
      content: 'Edit your search query here to find different matches.',
    },
    {
      target: '#tour-email-container',
      content: "Human-Level AI Email Generation; Just upload 3 past emails or messages you've written. Our system instantly trains a personalized AI model that replicates your exact style.",
    },
    {
      target: '#tour-writing-samples',
      content: 'Add writing samples here to help the AI match your style. You can add up to 3 samples.',
    },
    {
      target: '#tour-generate-emails',
      content: 'Click here to generate personalized outreach emails for your selected contacts.',
    },
    {
      target: '#tour-match-card-0',
      content: 'This is a match result. Hover on connecting reasons to see more, select the match to proceed with the outreach.',
    },
  ];

  // Only show these steps when step === 4
  const matchResultsTourTargets = [
    '#tour-search-query',
    '#tour-email-container',
    '#tour-writing-samples',
    '#tour-generate-emails',
    '#tour-match-card-0',
  ];
  const guidedTourSteps = step === 4
    ? allTourSteps.filter(s => matchResultsTourTargets.includes(s.target))
    : [];

  const [shouldAutoStartTour, setShouldAutoStartTour] = useState(false);

  useEffect(() => {
    if (step === 3 && !loading) {
      setLoadingPercentage(0);
    }
  }, [step, loading]);

  const [showSampleWarningModal, setShowSampleWarningModal] = useState(false);
  const [dontShowSampleWarningAgain, setDontShowSampleWarningAgain] = useState(false);
  const [pendingEmailContacts, setPendingEmailContacts] = useState(null); // Store contacts for forced generation

  // Sync 'Don't show again' from localStorage on mount
  useEffect(() => {
    const dontShow = localStorage.getItem('dontShowSampleWarningAgain') === 'true';
    setDontShowSampleWarningAgain(dontShow);
  }, []);

  // Called by MatchResults when user tries to generate emails with <3 samples
  const handleShowSampleWarningModal = (selectedContacts) => {
    setPendingEmailContacts(selectedContacts || null);
    setShowSampleWarningModal(true);
  };

  // Called when user clicks 'Go ahead anyway' in modal
  const handleGoAheadAnyway = () => {
    setShowSampleWarningModal(false);
    if (pendingEmailContacts && typeof handleForceGenerateEmails === 'function') {
      handleForceGenerateEmails(pendingEmailContacts);
    }
    setPendingEmailContacts(null);
  };

  // Add writingSamples state
  const [writingSamples, setWritingSamples] = useState([
    { subject: '', body: '' },
    { subject: '', body: '' },
    { subject: '', body: '' }
  ]);
  const [initialWritingSamples, setInitialWritingSamples] = useState(null);
  const [writingSamplesLoading, setWritingSamplesLoading] = useState(false);

  // Fetch writing samples on mount
  useEffect(() => {
    const fetchSamples = async () => {
      setWritingSamplesLoading(true);
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const samples = await userService.getWritingSamples(currentUser.uid);
        const normalized = (samples && Array.isArray(samples) && samples.length > 0)
          ? samples.concat([{}, {}, {}]).slice(0, 3).map(s => ({ subject: s.subject || '', body: s.body || '' }))
          : [{ subject: '', body: '' }, { subject: '', body: '' }, { subject: '', body: '' }];
        setWritingSamples(normalized);
        setInitialWritingSamples(normalized);
      } catch (err) {
        const emptySamples = [{ subject: '', body: '' }, { subject: '', body: '' }, { subject: '', body: '' }];
        setWritingSamples(emptySamples);
        setInitialWritingSamples(emptySamples);
      } finally {
        setWritingSamplesLoading(false);
      }
    };
    fetchSamples();
  }, []);

  const handleForceGenerateEmails = async (selectedContacts) => {
    if (selectedContacts && selectedContacts.length > 0) {
      // Build userData as in MatchResults
      const userData = {
        name: resumeData?.parsed?.resume?.name || '',
        intent: intentValue, // Use StepWizard's intentValue
        current_company_name: resumeData?.parsed?.resume?.current_company || '',
        position: resumeData?.parsed?.resume?.position || '',
        is_student: resumeData?.parsed?.resume?.is_student || false,
        professional_keywords: resumeData?.parsed?.resume?.professional_keywords || [],
        personal_keywords: resumeData?.parsed?.resume?.personal_keywords || [],
        experience: (resumeData?.parsed?.resume?.experience || []).map(exp => ({
          ...exp,
          description: Array.isArray(exp.description) ? exp.description.join('\n') : exp.description,
        })),
        education: resumeData?.parsed?.resume?.education || [],
        organizations: (resumeData?.parsed?.resume?.organizations || []).map(org => ({
          ...org,
          description: Array.isArray(org.description) ? org.description.join('\n') : org.description,
        })),
      };
      // Dummy copyToClipboard function
      const copyToClipboard = () => {};
      // Only non-empty writing samples
      const filteredWritingSamples = writingSamples.filter(ws => ws.subject.trim() || ws.body.trim());
      handleShowEmailModal({
        selectedContacts,
        userData,
        copyToClipboard,
        writingSamples: filteredWritingSamples
      });
    }
  };

  const scrollToEmailGenerator = () => {
    if (emailContainerRef.current) {
      const element = emailContainerRef.current;
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 3);
      
      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
      
      // Trigger pop animation after scroll completes
      setTimeout(() => {
        setEmailContainerAnimated(true);
        // Reset animation after it completes
        setTimeout(() => {
          setEmailContainerAnimated(false);
        }, 600);
      }, 800);
      
      // Hide button after clicking with 1 second delay
      setTimeout(() => {
        setButtonClicked(true);
      }, 600);

    }
  };

  const handleContactSelection = (contacts) => {
    setSelectedContacts(contacts);
    setShowFloatingButton(contacts.length > 0);
    // Reset button clicked state when contacts change
    if (contacts.length > 0) {
      setButtonClicked(false);
    }
  };

  return (
    <div className={`relative min-h-screen overflow-hidden ${MAIN_BG_GRADIENT}`}>
      {/* Mouse glow effect */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div id="mouse-glow" className="absolute w-72 h-72 bg-white/30 rounded-full blur-3xl -z-10 transition-transform"></div>
      </div>

      {/* AppBar Toggle Button */}
      <div className="fixed top-5 right-5 z-[10001]">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsAppBarVisible(!isAppBarVisible)}
          className="bg-black/50 backdrop-blur-sm text-white rounded-full p-2 shadow-lg hover:bg-black/70 transition-all"
          aria-label={isAppBarVisible ? "Hide menu" : "Show menu"}
        >
          {isAppBarVisible ? <CloseIcon className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </motion.button>
      </div>

      {/* AppBar */}
      <AnimatePresence>
        {isAppBarVisible && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <AppBar
              showUpgradeButton={!userSubscription || userSubscription.tier === 'starter'}
              showPricingModal={showPricingModal}
              setShowPricingModal={setShowPricingModal}
              userData={userSubscription}
              onHistoryItemClick={handleHistoryItemClick}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
      />

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />

      {showEmailModal && emailModalData && (
        <EmailGeneratorModals
          {...emailModalData}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* UI Blocker Modal with Progress Indicator */}
      <AnimatePresence>
        {step === 4 && !loading && loadingPercentage === 0 && (
          ((isAnyReasonLoading && !isLoadedFromHistory) ||
            (showBlockerModal && !isAnyReasonLoading && !isLoadedFromHistory)) && (
            <UIBlockerModal
              key="ui-blocker"
              message={"Rendering data..."}
              progressPercent={progressPercent}
              showGoToResults={(!isAnyReasonLoading && !isLoadedFromHistory)}
              onGoToResults={() => {
                setShowBlockerModal(false);
                if (shouldAutoStartTour && step === 3 && !tourVisible) {
                  setTourVisible(true);
                  setTourStepIndex(0);
                  setShouldAutoStartTour(false);
                  localStorage.removeItem('shouldAutoStartTour');
                }
              }}
            />
          )
        )}
      </AnimatePresence>

      {/* Guided Tour Overlay (always rendered at top level) */}
      <GuidedTour
        steps={guidedTourSteps}
        stepIndex={tourStepIndex}
        visible={tourVisible}
        onNext={() => setTourStepIndex(i => Math.min(i + 1, guidedTourSteps.length - 1))}
        onPrev={() => setTourStepIndex(i => Math.max(i - 1, 0))}
        onClose={() => { setTourVisible(false); setTourStepIndex(0); }}
      />
      {/* Take a Tour Button*/}
      <div className="fixed bottom-8 left-8 z-[10002]">
        <button
          className="group relative flex items-center justify-center rounded-full bg-black text-white font-semibold shadow hover:bg-gray-900 transition-all duration-200 text-sm h-10 w-10 hover:w-40 focus:w-40 overflow-hidden"
          onClick={() => { setTourVisible(true); setTourStepIndex(0); }}
          aria-label="Take a Tour"
          disabled={guidedTourSteps.length === 0}
          type="button"
        >
          <svg className="h-5 w-5 flex-shrink-0 transition-all duration-200 z-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.24 7.76l-4.24 1.41-1.41 4.24 4.24-1.41z" />
          </svg>
          <span
            className="ml-2 w-0 opacity-0 overflow-hidden transition-all duration-200 group-hover:w-auto group-hover:opacity-100 group-focus:w-auto group-focus:opacity-100 whitespace-nowrap"
          >
            Take a Tour
          </span>
        </button>
      </div>


      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-5xl mx-auto px-4 py-12"
      >
        {/* Background Animation Elements */}
        <motion.div
          className="absolute inset-0 -z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-24 h-24 bg-white/5 rounded-full blur-lg"
            animate={{
              x: [0, -80, 0],
              y: [0, 60, 0],
              scale: [1, 0.8, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
          />
        </motion.div>
        {/* Enhanced Step Indicator */}
        <div className="relative mb-16">
          <div className="flex items-center justify-center">
            {/* {[1, 2, 3, 4].map((stepNumber) => (
              <React.Fragment key={stepNumber}>
                <motion.div
                  custom={stepNumber - 1}
                  variants={stepIndicatorVariants}
                  initial="initial"
                  animate={stepNumber === step ? "active" : "animate"}
                  whileHover="hover"
                  whileTap="tap"
                  className={`relative flex items-center justify-center h-12 w-12 rounded-full cursor-pointer transition-all duration-300 ${
                    step >= stepNumber
                      ? 'bg-black text-white shadow-lg'
                      : 'bg-white/60 backdrop-blur-sm text-gray-400 border border-gray-200 hover:bg-white/80'
                  }`}
                  onClick={() => stepNumber <= step && goToStep(stepNumber)}
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: stepNumber * 0.1 + 0.2, type: "spring", stiffness: 300, damping: 20 }}
                  >
                    {stepNumber}
                  </motion.span>
                </motion.div>
                {stepNumber < 4 && (
                  <div className="relative h-1 w-16 mx-2">
                    <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                    <motion.div
                      custom={step > stepNumber}
                      variants={progressLineVariants}
                      initial="initial"
                      animate="animate"
                      className="absolute inset-0 bg-black rounded-full origin-left"
                    />
                  </div>
                )}
              </React.Fragment>
            ))} */}
          </div>

          {/* Search Query Display */}
          {loading && searchQuery && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 mx-auto bg-white/80 backdrop-blur-md rounded-full py-2.5 px-6 shadow-lg border border-gray-200/50 flex items-center space-x-3 w-[480px]"
            >
              <div className="flex items-center space-x-2">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <div className="h-4 w-px bg-gray-200"></div>
              </div>
              <p className="text-sm font-medium text-gray-700 truncate flex-1" title={searchQuery}>
                {searchQuery}
              </p>
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-2 h-2 rounded-full bg-green-500"
              />
            </motion.div>
          )}
        </div>

        {/* Enhanced Step Screens */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={stepTransition}
            className={step === 1
              ? "transform-gpu"
              : "bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 p-8 transform-gpu"}
          >
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <SearchPage onContinue={handleResumeContinue} />
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <SearchParameters
                parsedResume={parsedJson}
                searchRecommendations={searchRecommendations}
                onSearch={(query, goToChatbot) => {
                  setSearchQuery(query);
                  if (goToChatbot) {
                    setStep(3);
                  } else {
                    setStep(4);
                    handleSearch(query, matchLimit);
                  }
                }}
                onBack={() => { goToPrev(); setShowBlockerModal(false); }}
              />
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <ChatbotQuery
                onSubmit={chatData => { 
                  setChatbotAnswers(chatData); 
                  // If we have a final query from the chatbot, use it for the search
                  if (chatData.finalQuery) {
                    setSearchQuery(chatData.finalQuery);
                  }
                  setStep(4); 
                  handleSearch(chatData.finalQuery,matchLimit)
                }}
                initialQuery={searchQuery}
              />
            </motion.div>
          )}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[300px]">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center w-full max-w-md mx-auto"
                  >
                    <motion.img
                      src="/logo.png"
                      alt="Quis Lab Logo"
                      className="h-16 w-16 mx-auto mb-4"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />
                    <p className="text-sm text-gray-600 font-medium mb-4">{rotatingMessage}</p>
                    <div className="w-full max-w-xs mx-auto">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-black"
                          initial={{ width: 0 }}
                          animate={{ width: `${loadingPercentage}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">{loadingPercentage}%</p>
                    </div>
                  </motion.div>
                </div>
              ) : (
                <MatchResults
                  resumeData={resumeData}
                  matches={matches}
                  refinedSearch={refinedSearch}
                  onBack={() => { setStep(1); setShowBlockerModal(false); }}
                  isSubscribed={userSubscription?.tier !== 'starter' && userSubscription?.tier !== 'free'}
                  subscriptionTier={userSubscription?.tier}
                  subscriptionStatus={userSubscription?.subscription_status}
                  connecting_points={connectionPoints}
                  searchQuery={searchQuery}
                  chatbotAnswers={chatbotAnswers}
                  inversePrompts={inversePrompts}
                  onSubscribe={() => setShowPricingModal(true)}
                  onGenerateEmailsClick={handleShowEmailModal}
                  isLoadedFromHistory={isLoadedFromHistory}
                  totalFound={totalFound}
                  emailContainerRef={emailContainerRef}
                  onContactSelection={handleContactSelection}
                  emailContainerAnimated={emailContainerAnimated}
                  writingSamples={writingSamples}
                  setWritingSamples={setWritingSamples}
                  initialWritingSamples={initialWritingSamples}
                  writingSamplesLoading={writingSamplesLoading}
                  onGenerateEmails={async (selectedProfiles) => {
                    try {
                      // Get Firebase auth token
                      const { getAuth } = await import('firebase/auth');
                      const auth = getAuth();
                      const currentUser = auth.currentUser;

                      if (!currentUser) {
                        console.error('No authenticated user found');
                        return;
                      }

                      console.log('Current user:', {
                        uid: currentUser.uid,
                        email: currentUser.email,
                        displayName: currentUser.displayName
                      });


                      const authToken = await currentUser.getIdToken();
                      const headers = {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      };

                      // Prepare selected profiles data with reasons
                      console.log('Processing profiles - checking for reasons...');
                      const selectedProfilesData = selectedProfiles.map(profile => {
                        const profileData = {
                          id: profile.id || '',
                          name: profile.name || '',
                          position: profile.position || '',
                          company: profile.company || '',
                          email: profile.email || '',
                          url: profile.url || profile.linkedin || '',
                          // Include the reason if it exists
                          reason: profile.reason || '',
                          // Include the matched keywords if they exist
                          matched_keywords: profile.matched_keywords || []
                        };
                        return profileData;
                      });

                      // Get current user data
                      const userProfile = {
                        uid: currentUser.uid,
                        email: currentUser.email,
                        displayName: currentUser.displayName,
                        // Add any other relevant user data from resumeData if available
                        ...(resumeData?.parsed?.connectionPoints && {
                          connection_points: Array.isArray(resumeData.parsed.connectionPoints)
                            ? resumeData.parsed.connectionPoints
                            : resumeData.parsed.connectionPoints.connection_points || []
                        })
                      };

                      // Prepare the request payload
                      const requestPayload = {
                        originalPrompt: searchQuery,
                        selectedProfiles: selectedProfilesData,
                        userData: userProfile,
                        timestamp: new Date().toISOString()
                      };

                      // Save selected profiles to GCS with reasoning data
                      const response = await fetch(
                        `${process.env.REACT_APP_REFINE_QUERY_URL}/api/v2/save-selected-profiles`,
                        {
                          method: 'POST',
                          headers: headers,
                          credentials: 'include',
                          body: JSON.stringify(requestPayload)
                        }
                      );

                      console.log('=== RESPONSE ===');
                      console.log('Status:', response.status, response.statusText);

                      const responseData = await response.json().catch(e => ({
                        error: 'Failed to parse JSON response',
                        details: e.message
                      }));

                      if (!response.ok) {
                        console.error('Error response data:', responseData);
                        throw new Error(responseData.message || 'Failed to save profiles');
                      }

                      console.log('Successfully saved selected profiles with reasoning data');
                      console.log('Response data:', responseData);
                      return responseData;
                    } catch (error) {
                      console.error('Error saving selected profiles:', error);
                      // Log more detailed error information
                      if (error.response) {
                        console.error('Error response data:', error.response.data);
                        console.error('Error status:', error.response.status);
                        console.error('Error headers:', error.response.headers);
                      } else if (error.request) {
                        console.error('No response received:', error.request);
                      } else {
                        console.error('Error message:', error.message);
                      }
                      // Don't block the user if saving fails
                    }
                  }}
                  loadingReasons={loadingReasons}
                  setLoadingReasons={setLoadingReasons}
                  matchReasons={matchReasons}
                  setMatchReasons={setMatchReasons}
                  connectionReasons={connectionReasons}
                  setConnectionReasons={setConnectionReasons}
                  visibleCount={visibleCount}
                  setVisibleCount={setVisibleCount}
                  step={step}
                  userSubscription={userSubscription}
                  onAllVisibleReasonsLoaded={() => {
                    console.log('All visible reasons loaded, hiding UI blocker');
                    // Add a small delay to ensure state updates are complete
                    setTimeout(() => {
                      setShowBlockerModal(false);
                    }, 100);
                  }}
                  onOpenEmailInfoModal={setShowEmailModal}
                  onEditSearchQuery={handleEditSearchQuery}
                  searchRecommendations={searchRecommendations}
                  onShowSampleWarningModal={dontShowSampleWarningAgain ? undefined : handleShowSampleWarningModal}
                  onForceGenerateEmails={handleForceGenerateEmails}
                  onIntentChange={setIntentValue}
                />
              )}
            </motion.div>
          )}
        </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Floating Generate Email Button */}
      <AnimatePresence>
        {showFloatingButton && step === 3 && !buttonClicked && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={scrollToEmailGenerator}
            className="fixed bottom-6 left-0 right-0 mx-auto w-fit z-50 bg-black text-white px-4 py-3 rounded-full shadow-2xl hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 group"
            style={{
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-sm">Generate Emails</span>
            </div>
            <div className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
              {selectedContacts.length}
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating Action Button with Help Dialog */}
      <div className="fixed bottom-8 right-8 z-50">
        {showHelpDialog && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-16 right-0 w-80 bg-white/80 backdrop-blur-md rounded-xl shadow-xl overflow-hidden transform transition-all duration-300 ease-in-out border border-gray-200/50"
          >
            <div className="p-4 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-gray-100/50">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Need help?</h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowHelpDialog(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>
            </div>
            <div className="p-4">
              {submitSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-4"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 10 }}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <p className="text-gray-600">Message sent! We'll get back to you soon by email.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleMessageSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Send us a message
                    </label>
                    <textarea
                      id="message"
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none bg-white/50 backdrop-blur-sm transition-all duration-200"
                      placeholder="Type your message here..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting || !message.trim()}
                    className={`w-full py-2.5 px-4 rounded-lg text-white font-medium transition-all duration-200 ${isSubmitting || !message.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-black hover:bg-gray-800 shadow-md'
                      }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </div>
                    ) : (
                      'Send Message'
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleFabClick}
          className={`bg-black text-white rounded-full p-4 shadow-lg hover:bg-gray-800 transition-all duration-300 ease-in-out ${isRotating ? 'rotate-180' : ''
            }`}
          aria-label="Message"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-6 w-6 transition-transform duration-300 ${showHelpDialog ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={showHelpDialog
                ? "M6 18L18 6M6 6l12 12"
                : "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-5l-5 5v-5z"
              }
            />
          </svg>
        </motion.button>
      </div>

      {/* Writing Sample Warning Modal (overlays whole step) */}
      <Modal
        open={showSampleWarningModal}
        onClose={() => setShowSampleWarningModal(false)}
        title="Add More Writing Samples for Best Results"
        showDontShowAgain={true}
        dontShowAgainChecked={dontShowSampleWarningAgain}
        onDontShowAgainChange={checked => {
          setDontShowSampleWarningAgain(checked);
          localStorage.setItem('dontShowSampleWarningAgain', checked ? 'true' : 'false');
        }}
        actions={
          <>
            <button
              className="bg-black text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              onClick={() => setShowSampleWarningModal(false)}
              autoFocus
            >
              OK
            </button>
            <button
              className="ml-2 bg-gray-200 text-black px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-300 transition-colors"
              onClick={handleGoAheadAnyway}
            >
              Go ahead anyway
            </button>
          </>
        }
      >
        <div className="text-gray-700 text-base">
          <p className="mb-3">
            For the best personalized AI emails, we recommend uploading <b>3 writing samples</b> that reflect your style. This helps Quis AI craft messages that sound like you.
          </p>
          <ul className="list-disc pl-5 mb-3 text-sm">
            <li>More samples = more accurate, natural-sounding emails</li>
            <li>You can skip this, but results may be less personalized</li>
          </ul>
          <p className="text-xs text-gray-500">You can choose not to see this message again.</p>
        </div>
      </Modal>
    </div>
  );
};

export default StepWizard;